// app/features/ai-agent-conversations/services_and_hooks/useConversationsQuery.ts
import { useQuery } from '@tanstack/react-query';

import { useDataFetchingHook } from '@/app/shared/hooks/useDataFetchingHook';

import { conversationsService } from './conversationsService';

export function useConversationsQuery(brandId: string | null) {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['ai-agent-conversations', brandId],
    queryFn: () => {
      if (!brandId) throw new Error('Brand ID is required');
      return conversationsService.getConversationsByBrand(brandId);
    },
    enabled: !!brandId,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });

  useDataFetchingHook({ 
    isLoading, 
    isError, 
    error, 
    errorMessage: error?.message || '' 
  });

  return {
    conversations: data || [],
    isLoading,
    isError,
    error,
    refetch
  };
}

export function useConversationsByOrganizationQuery(organizationId: string | null) {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['ai-agent-conversations-org', organizationId],
    queryFn: () => {
      if (!organizationId) throw new Error('Organization ID is required');
      return conversationsService.getConversationsByOrganization(organizationId);
    },
    enabled: !!organizationId,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });

  useDataFetchingHook({ 
    isLoading, 
    isError, 
    error, 
    errorMessage: error?.message || '' 
  });

  return {
    conversations: data || [],
    isLoading,
    isError,
    error,
    refetch
  };
}

export function useConversationByIdQuery(conversationId: string | null) {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['ai-agent-conversation', conversationId],
    queryFn: () => {
      if (!conversationId) throw new Error('Conversation ID is required');
      return conversationsService.getConversationById(conversationId);
    },
    enabled: !!conversationId,
    staleTime: 10 * 1000,
  });

  useDataFetchingHook({ 
    isLoading, 
    isError, 
    error, 
    errorMessage: error?.message || '' 
  });

  return {
    conversation: data,
    isLoading,
    isError,
    error,
    refetch
  };
}

